/**
 * Favorites Button Component
 * Heart icon button that toggles favorite status with toast notifications
 */



import { useState, useCallback } from 'react';
import { Heart } from 'lucide-react';
import { useFavorites } from '@/hooks/use-favorites';
import { AddFavoritePayload, FavoriteType } from '@/types/favorites';
import { Button } from '@/components/ui/button';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';

interface FavoritesButtonProps {
  id: string;
  type: FavoriteType;
  name: string;
  slug: string;
  metadata?: AddFavoritePayload['metadata'];
  variant?: 'default' | 'outline' | 'ghost' | 'secondary';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  showLabel?: boolean;
  className?: string;
}

export function FavoritesButton({
  id,
  type,
  name,
  slug,
  metadata,
  variant = 'outline',
  size = 'icon',
  showLabel = false,
  className,
}: FavoritesButtonProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const [isPending, setIsPending] = useState(false);

  const favorited = isFavorite(id, type);
  const label = favorited ? 'Remove from favorites' : 'Add to favorites';

  const handleToggle = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      setIsPending(true);

      try {
        const payload: AddFavoritePayload = { id, type, name, slug, metadata };
        const success = toggleFavorite(payload);

        if (success) {
          if (favorited) {
            toast.success(`${name} removed from favorites`);
          } else {
            toast.success(`${name} added to favorites`);
          }
        }
      } catch (error) {
        toast.error('Failed to update favorites');
        console.error(error);
      } finally {
        setIsPending(false);
      }
    },
    [id, type, name, slug, metadata, toggleFavorite, favorited]
  );

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant={favorited ? 'default' : variant}
            size={size}
            onClick={handleToggle}
            disabled={isPending}
            className={className}
            aria-label={label}
          >
            <Heart
              className={`h-4 w-4 ${favorited ? 'fill-current' : ''} transition-all`}
            />
            {showLabel && (
              <span className="ml-2">
                {favorited ? 'Favorited' : 'Add to Favorites'}
              </span>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{label}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
